/**
 * F-170 (B4.4) — Order form for non-time-based services.
 *
 * Triggered from `ProfileFeed.ServiceCard` when a preset is non-bookable
 * (no `duration_minutes`). Collects address + comment up-front and posts
 * `POST /orders/` with `preset_id`; the backend (B0.12) spawns a
 * dedicated chat thread + `order_proposal` system message and returns
 * its `chat_id`, which we hand back via `onCreated` for navigation.
 */
import { useState } from 'react';
import { Button, Modal, Textarea } from '@/components/atoms';
import { toast } from '@/components/atoms/Toast';
import { useHapticFeedback } from '@/hooks';
import { createOrder } from '@/api/orders';
import { logger } from '@/utils/logger';
import type { ServicePreset } from '@/types/domain';

export interface ServiceOrderFormProps {
  open: boolean;
  preset: ServicePreset;
  specialistSlug: string;
  onClose: () => void;
  /** Called with the newly created per-order chat id so the caller can
   *  navigate into the thread (the spec routes through `/chats/<id>`). */
  onCreated: (chatId: number) => void;
}

const MAX_COMMENT = 500;
const MAX_ADDRESS = 500;

export function ServiceOrderForm({
  open,
  preset,
  onClose,
  onCreated,
}: ServiceOrderFormProps): JSX.Element | null {
  // location_kind drives the address requirement:
  //   delivery → майстер їде до клієнта → address required
  //   onsite → клієнт може прийти до майстра; address optional
  //   online → no address ever
  const locationKind = preset.location_kind ?? 'onsite';
  const addressRequired = locationKind === 'delivery';
  const showAddress = locationKind !== 'online';

  const [address, setAddress] = useState('');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  // F-180 C6: inline address error instead of a transient toast.
  const [addressError, setAddressError] = useState<string | undefined>();
  // F-180 C8: haptic on successful place.
  const haptic = useHapticFeedback();

  if (!open) return null;

  const submit = async (): Promise<void> => {
    if (addressRequired && !address.trim()) {
      setAddressError('Вкажи адресу — майстер приїде до тебе');
      return;
    }
    setBusy(true);
    try {
      const order = await createOrder({
        preset_id: preset.id,
        address_text: address.trim() || undefined,
        comment: comment.trim() || undefined,
      });
      const chatId = order.chat_id ?? order.id;
      haptic.notification('success');
      toast.success('Замовлення створено');
      onCreated(chatId);
    } catch (err) {
      logger.warn('createOrder failed', err);
      toast.error('Не вдалось створити замовлення');
    } finally {
      setBusy(false);
    }
  };

  const addressLabel =
    locationKind === 'delivery'
      ? 'Адреса (обовʼязково)'
      : locationKind === 'onsite'
        ? 'Адреса (необовʼязково)'
        : '';

  return (
    <Modal
      open
      title={`Замовити · ${preset.title}`}
      onClose={busy ? () => undefined : onClose}
      actions={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Скасувати
          </Button>
          <Button onClick={() => void submit()} disabled={busy}>
            {busy ? 'Надсилаю…' : 'Замовити'}
          </Button>
        </>
      }
    >
      <p style={{ margin: '0 0 12px', color: 'var(--fg-muted)', fontSize: 13 }}>
        {locationKind === 'delivery'
          ? 'Майстер приїде до тебе. Опиши, де зустрітись.'
          : locationKind === 'onsite'
            ? 'Можеш приїхати до майстра або погодити виїзд у коментарі.'
            : 'Деталі обговорите в чаті після підтвердження.'}
      </p>
      {showAddress ? (
        <Textarea
          label={addressLabel}
          value={address}
          error={addressError}
          onChange={(e) => {
            setAddress(e.target.value.slice(0, MAX_ADDRESS));
            setAddressError(undefined);
          }}
          rows={2}
          placeholder="Місто, вулиця, орієнтири…"
        />
      ) : null}
      <Textarea
        label={`Коментар (необовʼязково) · ${comment.length}/${MAX_COMMENT}`}
        value={comment}
        onChange={(e) => setComment(e.target.value.slice(0, MAX_COMMENT))}
        rows={3}
        placeholder="Що важливо знати майстру?"
      />
    </Modal>
  );
}
